// src/pages/Dashboard.tsx
import React, { useMemo, useState, useRef } from 'react';
import ProductHeader from '../components/ProductHeader';
import HeaderActions from '../components/HeaderActions';
import DashboardSwitch, { DashSection } from '../components/DashboardSwitch';

import { useNotifications } from '../hooks/useNotifications';
import { useQuestions } from '../hooks/useQuestions';
import { useExpenses } from '../hooks/useExpenses';
import { useInbounds } from '../hooks/useInbounds';
import { useTableReset } from '../hooks/useTableReset';
import { formatDateTime } from '../utils/dates';

import type { Notification as OrderNotif } from '../types/Notification';
import { getStatusMeta } from '../ui/design';
import { useSmartTableFilter } from '../hooks/useSmartTableFilter';
import { useProductManager } from '../hooks/useProductManager';

// Вынесенные секции (презентационные компоненты)
import RevenueSection from './dashboard/sections/RevenueSection';
import ExpenseSection from './dashboard/sections/ExpenseSection';
import OrdersSection from './dashboard/sections/OrdersSection';
import InboundSection from './dashboard/sections/InboundSection';
import NotificationsArea, { NotificationsAreaHandle } from '../components/NotificationsArea';
import Warehouse from '../components/Warehouse';

// фиксированный порядок статусов заказов — стабильная константа на уровне модуля
const ORDER_STATUS_LIST: OrderNotif['status'][] = [
  'new',
  'processing',
  'ready',
  'shipped',
  'canceled',
  'returned',
];

const Dashboard: React.FC = () => {
  const [section, setSection] = useState<DashSection>('income');
  const notifRef = useRef<NotificationsAreaHandle>(null);
  
// уведомления (только счётчик для хедера)
// уведомления (только счётчик для хедера)
const { unread } = useNotifications(10);

// товары для экрана "Склад" — берём items из соответствующих стооров
// тип StoreName у тебя в utils/db; чтобы не тянуть его сюда, используем as any.
const coilsPM   = useProductManager<any>('ignitionCoils'   as any);
const plugsPM   = useProductManager<any>('sparkPlugs'      as any);
const wiresPM   = useProductManager<any>('wires'           as any); // если у тебя иное имя (например, 'wireTips' или 'wireSets'), подставь его сюда
const sensorsPM = useProductManager<any>('sensors'         as any);
const clocksPM  = useProductManager<any>('steeringClocks'  as any);

const coils   = coilsPM.items   ?? [];
const plugs   = plugsPM.items   ?? [];
const wires   = wiresPM.items   ?? [];
const sensors = sensorsPM.items ?? [];
const clocks  = clocksPM.items  ?? [];


// полный список для графиков/таблицы
const { items: allOrders } = useNotifications(10000);

// сообщения покупателей (только счётчик для хедера)
const { unread: qUnread } = useQuestions(5);

// версии колонок для форс-перемонтирования таблиц
const [ordersColsVer,   resetOrdersColumns]   = useTableReset('table.ordersItems.cols.v3');
const [inboundsColsVer, resetInboundsColumns] = useTableReset('table.inbounds.cols.v4');
const [expensesColsVer, resetExpensesColumns] = useTableReset('table.expenses.cols.v1');

  // --- Заказы: поиск/чипы
  const {
    searchText: ordersSearch,
    setSearchText: setOrdersSearch,
    toggleStatus: toggleOrderStatus,
    isStatusActive: isOrderStatusActive,
  } = useSmartTableFilter(allOrders, { statusField: 'status' });

  const fmtOrderDisplay = (o: any): string => {
    const raw = String(o?.orderNumber ?? o?.number ?? o?.id ?? '').trim();
    if (!raw) return 'ord-';
    const digits = raw.match(/\d{3,}/);
    if (digits) return `ord-${digits[0].toLowerCase()}`;
    const alnum = raw.replace(/[^a-z0-9]/gi, '').toLowerCase();
    return `ord-${(alnum || '0000000000').slice(-10)}`;
  };

  const ordersFiltered = useMemo(() => {
    const normalize = (s: any) =>
      String(s ?? '')
        .toLowerCase()
        .replace(/\u00a0/g, ' ')
        .replace(/[\u200b-\u200d\u2060\ufeff]/g, '')
        .replace(/[\u2010-\u2015\u2212\u2043\uFE58\uFE63\uFF0D]/g, '-')
        .trim();

    const q = normalize(ordersSearch);
    const qNoPref = q.replace(/^ord-/, '');
    const anyStatusActive = ORDER_STATUS_LIST.some((st) => isOrderStatusActive(st as any));
    const norm = (v: any) => String(v ?? '').toLowerCase();

    return (allOrders as any[]).filter((o) => {
      if (anyStatusActive && !isOrderStatusActive(String(o?.status))) return false;
      if (!q) return true;

      const disp = fmtOrderDisplay(o);
      if (disp.includes(q) || disp.includes(qNoPref)) return true;

      const rawNo = norm(o?.orderNumber ?? o?.number ?? '');
      if (rawNo.includes(q) || rawNo.includes(qNoPref)) return true;

      const idStr = norm(o?.id);
      const idTail = idStr.replace(/[^a-z0-9]/g, '').slice(-10);
      if (idStr.includes(q) || idTail.includes(qNoPref)) return true;

      if (
        norm(o?.status).includes(q) ||
        norm(o?.itemType).includes(q) ||
        norm(o?.quality).includes(q) ||
        norm(o?.total).includes(q) ||
        formatDateTime(o?.createdAt).toLowerCase().includes(q)
      ) {
        return true;
      }

      const items: any[] = Array.isArray(o?.items) ? o.items : [];
      for (const it of items) {
        const quality =
          it?.isOriginal === true
            ? 'оригинал'
            : it?.isOriginal === false
            ? 'копия'
            : String(it?.quality ?? '').toLowerCase();

        if (
          norm(it?.sku).includes(q) ||
          norm(it?.name ?? it?.product).includes(q) ||
          norm(it?.category ?? it?.type).includes(q) ||
          String(quality).includes(q) ||
          norm(it?.quantity ?? it?.qty).includes(q) ||
          norm(it?.price).includes(q) ||
          norm(it?.total).includes(q)
        ) {
          return true;
        }
      }

      return false;
    });
  }, [allOrders, ordersSearch, isOrderStatusActive]);

  // Счётчики заказов (для чипов)
  const orderStatusCountsAll = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const n of allOrders) {
      const k = n?.status ?? 'unknown';
      acc[k] = (acc[k] || 0) + 1;
    }
    return acc;
  }, [allOrders]);

  const visibleOrderStatuses = useMemo(
    () => ORDER_STATUS_LIST.filter((st) => (orderStatusCountsAll[st] ?? 0) > 0),
    [orderStatusCountsAll]
  );

  // -------- Серии для графиков (выручка/расход/кол-во/поступления)
  const revenueSeries = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const o of allOrders) {
      const status = o.status ?? '';
      // Only sold orders count as income; canceled/returned deduct
      let delta = 0;
      const amt = (o as any).effectiveTotal ?? o.total ?? 0;
      if (status === 'shipped') delta = amt;
      else if (status === 'canceled' || status === 'returned') delta = -amt;
      else continue; // pending/new/processing — not counted yet
      const key = new Date(o.createdAt).toISOString().slice(0, 10);
      byDate.set(key, (byDate.get(key) || 0) + delta);
    }
    const dates = Array.from(byDate.keys()).sort();
    let sum = 0;
    return dates.map((date) => {
      const revenue = byDate.get(date) || 0;
      sum += revenue;
      return { date, revenue, cumulative: sum };
    });
  }, [allOrders]);

  const ordersSeries = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const o of allOrders) {
      const key = new Date(o.createdAt).toISOString().slice(0, 10);
      byDate.set(key, (byDate.get(key) || 0) + 1);
    }
    const dates = Array.from(byDate.keys()).sort();
    let sum = 0;
    return dates.map((date) => {
      const count = byDate.get(date) || 0;
      sum += count;
      return { date, count, cumulative: sum };
    });
  }, [allOrders]);

  // --- Расходы
  const { items: expItems, reload: reloadExpenses } = useExpenses(10000);

  const expenseSeries = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const e of expItems) {
      const d = new Date(e.createdAt);
      const key = d.toISOString().slice(0, 10);
      byDate.set(key, (byDate.get(key) || 0) + (e.total || 0));
    }
    const dates = Array.from(byDate.keys()).sort();
    let sum = 0;
    return dates.map((date) => {
      const expense = byDate.get(date) || 0;
      sum += expense;
      return { date, expense, cumulative: sum };
    });
  }, [expItems]);

  const EXP_STATUS_META: Record<string, { label: string; chip: { bg: string; fg: string } }> = {
    planned: { label: 'Запланировано', chip: { bg: '#f0f5ff', fg: '#2f54eb' } },
    processing: { label: 'В процессе', chip: { bg: '#fffbe6', fg: '#faad14' } },
    paid: { label: 'Оплачено', chip: { bg: '#f6ffed', fg: '#52c41a' } },
    canceled: { label: 'Отменено', chip: { bg: '#fff1f0', fg: '#ff4d4f' } },
  };
  const EXP_STATUS_LIST = ['planned', 'processing', 'paid', 'canceled'] as const;

  const [expActiveStatuses, setExpActiveStatuses] = useState<string[]>([]);
  const toggleExpStatus = (key: string) =>
    setExpActiveStatuses((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const expStatusCounts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const r of expItems) {
      const k = String(r?.status ?? 'unknown');
      acc[k] = (acc[k] || 0) + 1;
    }
    return acc;
  }, [expItems]);

  const [expenseSearch, setExpenseSearch] = useState('');
  const expensesFiltered = useMemo(() => {
    const q = expenseSearch.trim().toLowerCase();
    const active = new Set(expActiveStatuses);

    return (expItems as any[]).filter((r) => {
      if (active.size > 0 && !active.has(String(r?.status ?? ''))) return false;
      if (!q) return true;

      const id = String(r?.id ?? r?.number ?? '').toLowerCase();
      const dateStr = new Date(r?.createdAt).toLocaleString('ru-RU').toLowerCase();
      const category = String(r?.category ?? r?.type ?? '').toLowerCase();
      const supplier = String(r?.supplier ?? '').toLowerCase();
      const note = String(r?.note ?? r?.description ?? '').toLowerCase();
      const status = String(r?.status ?? '').toLowerCase();
      const total = String(r?.total ?? r?.totalCost ?? '').toLowerCase();

      if (
        id.includes(q) ||
        dateStr.includes(q) ||
        category.includes(q) ||
        supplier.includes(q) ||
        note.includes(q) ||
        status.includes(q) ||
        total.includes(q)
      )
        return true;

      const lines: any[] = Array.isArray(r?.lines) ? r.lines : [];
      for (const l of lines) {
        const sku = String(l?.sku ?? '').toLowerCase();
        const lname = String(l?.name ?? '').toLowerCase();
        const qty = String(l?.qty ?? l?.quantity ?? '').toLowerCase();
        const unit = String(l?.unitCost ?? '').toLowerCase();
        const ltotal = String(l?.totalCost ?? '').toLowerCase();
        if (sku.includes(q) || lname.includes(q) || qty.includes(q) || unit.includes(q) || ltotal.includes(q)) {
          return true;
        }
      }
      return false;
    });
  }, [expItems, expenseSearch, expActiveStatuses]);

  // --- Поступления
  const { items: inboundItems, reload: reloadInbounds } = useInbounds(10000);

  const [inboundSearch, setInboundSearch] = useState('');
  const [inboundActiveStatuses, setInboundActiveStatuses] = useState<string[]>([]);
  const toggleInboundStatus = (k: string) =>
    setInboundActiveStatuses((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const filteredInbounds = useMemo(() => {
    const q = inboundSearch.trim().toLowerCase();
    const active = new Set(inboundActiveStatuses);

    const matchLine = (l: any) => {
      const sku = String(l?.sku ?? '').toLowerCase();
      const name = String(l?.name ?? '').toLowerCase();
      const quality = String(l?.quality ?? '').toLowerCase();
      const qty = String(l?.qty ?? l?.quantity ?? '').toLowerCase();
      const unit = String(l?.unitCost ?? '').toLowerCase();
      const total = String(l?.totalCost ?? '').toLowerCase();
      return sku.includes(q) || name.includes(q) || quality.includes(q) || qty.includes(q) || unit.includes(q) || total.includes(q);
    };

    return (inboundItems as any[])
      .map((ib) => {
        if (active.size > 0) {
          const st = String(ib?.status ?? 'unknown');
          if (!active.has(st)) return null;
        }
        if (!q) return ib;

        const idStr = String(ib?.id ?? '').toLowerCase();
        const supplierStr = String(ib?.supplier ?? '').toLowerCase();
        const statusStr = String(ib?.status ?? '').toLowerCase();
        const dateStr = new Date(ib?.createdAt).toLocaleString('ru-RU').toLowerCase();
        const totalDocStr = String(ib?.totalCost ?? '').toLowerCase();
        const qtyDocStr = String(ib?.qty ?? '').toLowerCase();

        const docMatch =
          idStr.includes(q) ||
          supplierStr.includes(q) ||
          statusStr.includes(q) ||
          dateStr.includes(q) ||
          totalDocStr.includes(q) ||
          qtyDocStr.includes(q);

        if (docMatch) return ib;

        const lines: any[] = Array.isArray(ib?.lines) ? ib.lines : [];
        const matched = lines.filter(matchLine);
        if (matched.length) return { ...ib, lines: matched };

        return null;
      })
      .filter(Boolean) as any[];
  }, [inboundItems, inboundSearch, inboundActiveStatuses]);

  const filteredInboundsRowsCount = useMemo(() => {
    let sum = 0;
    for (const ib of filteredInbounds as any[]) {
      const lines = Array.isArray(ib?.lines) ? ib.lines.length : 0;
      sum += lines > 0 ? lines : 1;
    }
    return sum;
  }, [filteredInbounds]);

  const inboundStatusLineCounts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const ib of inboundItems) {
      const key = String((ib as any)?.status ?? 'unknown');
      const lines = Array.isArray((ib as any)?.lines) ? (ib as any).lines.length : 0;
      const add = lines > 0 ? lines : 1;
      acc[key] = (acc[key] || 0) + add;
    }
    return acc;
  }, [inboundItems]);

  const inboundSeries = useMemo(() => {
    const byDate = new Map<string, number>();
    for (const x of inboundItems) {
      const key = new Date(x.createdAt).toISOString().slice(0, 10);
      byDate.set(key, (byDate.get(key) || 0) + 1);
    }
    const dates = Array.from(byDate.keys()).sort();
    let sum = 0;
    return dates.map((date) => {
      const count = byDate.get(date) || 0;
      sum += count;
      return { date, count, cumulative: sum };
    });
  }, [inboundItems]);

  // Метаданные чипов поступлений
  const INBOUND_META = [
    { key: 'ready', label: 'Готово', fg: '#1677ff', bg: '#e6f4ff' },
    { key: 'processing', label: 'В процессе', fg: '#faad14', bg: '#fffbe6' },
    { key: 'in_transit', label: 'На пути', fg: '#13c2c2', bg: '#e6fffb' },
  ] as const;

  return (
    <div style={{ padding: '20px', overflowX: 'hidden' }}>
      <ProductHeader
            title={<h2 style={{ margin: 0 }}>Панель управления</h2>}
        center={<DashboardSwitch value={section} onChange={setSection} uiSize="sm" />}
        addButton={
        <HeaderActions
          ordersCount={unread}
          onOpenNotifications={() => notifRef.current?.openOrders()}
          messagesCount={qUnread}
          onOpenMessages={() => notifRef.current?.openMessages()}
        />
        }
      />

      {section === 'income' && (
        <RevenueSection ordersFiltered={ordersFiltered as any[]} revenueSeries={revenueSeries} />
      )}

      {section === 'expense' && (
        <ExpenseSection
          expensesFiltered={expensesFiltered as any[]}
          expenseSeries={expenseSeries}
          expItems={expItems as any[]}
          expStatusCounts={expStatusCounts}
          expActiveStatuses={expActiveStatuses}
          onToggleExpStatus={toggleExpStatus}
          expenseSearch={expenseSearch}
          onExpenseSearchChange={setExpenseSearch}
          onResetColumns={resetExpensesColumns}
          colsVer={expensesColsVer}
          EXP_STATUS_META={EXP_STATUS_META}
          EXP_STATUS_LIST={[...EXP_STATUS_LIST]}
          onExpenseCreated={reloadExpenses}
        />
      )}

      {section === 'orders' && (
        <OrdersSection
          ordersFiltered={ordersFiltered}
          ordersSeries={ordersSeries}
          visibleOrderStatuses={visibleOrderStatuses}
          orderStatusCountsAll={orderStatusCountsAll}
          isOrderStatusActive={(st) => isOrderStatusActive(st as any)}
          onToggleOrderStatus={(key) => toggleOrderStatus(key as any)}
          ordersSearch={ordersSearch}
          onOrdersSearchChange={setOrdersSearch}
          onResetColumns={resetOrdersColumns}
          colsVer={ordersColsVer}
          getStatusMeta={getStatusMeta as any}
          ORDER_STATUS_LIST={[...ORDER_STATUS_LIST]}
        />
      )}

      {section === 'inbound' && (
        <InboundSection
          filteredInbounds={filteredInbounds as any[]}
          inboundSeries={inboundSeries}
          inboundStatusLineCounts={inboundStatusLineCounts}
          inboundActiveStatuses={inboundActiveStatuses}
          onToggleInboundStatus={toggleInboundStatus}
          inboundSearch={inboundSearch}
          onInboundSearchChange={setInboundSearch}
          onResetColumns={resetInboundsColumns}
          rowsCount={filteredInboundsRowsCount}
          colsVer={inboundsColsVer}
          INBOUND_META={INBOUND_META as any}
          onInboundCreated={reloadInbounds}
        />
      )}
      
      {/* СКЛАД — новый раздел внутри дэшборда */}
      {section === 'inventory' && (
        <Warehouse
          coils={coils}
          plugs={plugs}
          wires={wires}
          sensors={sensors}
          clocks={clocks}
        />
      )}
      <NotificationsArea ref={notifRef} />
    </div>
  );
};

export default Dashboard;
